import React from 'react';
import { TFunction } from 'i18next';
import { Trans } from 'react-i18next';

// Matches "<1>, <2>..."".
const OPENING_TAG = /<\d+>/g;

/**
 * Trans components for the positional tags of a translated text: the tags are
 * given in order of appearance, the last one serving any further tags.
 *
 * transTags("Some <1>text</1> and <3>other text</3>", <span lang="en" />, <strong />)
 * gives [, <span lang="en" />, , <strong />].
 *
 * transTags("<0>Some text</0> and <2>other text</2>", <span lang="en" />, <strong />)
 * gives [<span lang="en" />, , <strong />].
 */
export const transTags = (text: string, ...tags: React.ReactElement[]) =>
    (text.match(OPENING_TAG) ?? ([] as string[])).reduce<React.ReactElement[]>(
        (components, tag, i) =>
            Object.assign(components, {
                [tag.slice(1, -1)]: tags[Math.min(i, tags.length - 1)],
            }),
        [],
    );

/**
 * Removes the positional tags where only plain text fits.
 * Matches "<1>" and "</1>" in "edit {name} in <1>Illuminate</1>".
 */
export const stripTransTags = (text: string) => text.replace(/<\/?\d+>/g, '');

interface TransTextProps {
    /** Already translated text, e.g. t(key, values) or one "|"-separated step. */
    text: string;
    /** Elements for the text's tags, as in transTags. */
    tags: React.ReactElement[];
}

/** Renders the tags of an already translated text, for keys built at runtime. */
export const TransText: React.FunctionComponent<TransTextProps> = ({
    text,
    tags,
}) => (
    <Trans
        defaults={text}
        components={transTags(text, ...tags)}
        t={(() => text) as unknown as TFunction}
    />
);
